import {useCallback, useMemo} from 'react';

import {accountRefetchForEvent} from '../../../account/hooks/use-account-update-refetch';
import {contactRefetchForEvent} from '../../../contact/hooks/use-contact-update-refetch';
import type {PeepType} from '../../../types';

import {useEditAccountBusinessCardField} from '../graphql/hooks/use-edit-account-business-card-field';
import {useEditContactBusinessCardField} from '../graphql/hooks/use-edit-contact-business-card-field';
import type {
    BusinessCardFieldHandlers,
    BusinessCardPatchInput,
    PatchAddressInput,
    PatchEmailInput,
    PatchPhoneInput,
    PatchUrlInput,
    RowErrorMessage,
} from '../types';

function getFieldErrorMessage(input: BusinessCardPatchInput): string {
    if (input.phoneValue) {
        return 'Invalid phone number';
    }
    if (input.emailValue !== undefined) {
        return 'Invalid email address';
    }
    if (input.urlInput !== undefined) {
        return 'Invalid URL';
    }
    if (input.addressInput) {
        return 'Invalid address';
    }

    // For field name updates that error
    return 'Cannot save this value. Please check formatting.';
}

export function useBusinessCardFieldHandlers(
    entityId: string,
    peepType: PeepType,
    setRowErrorMessage: (errorMessage: RowErrorMessage | null) => void
): BusinessCardFieldHandlers {
    const accountMutation = useEditAccountBusinessCardField();
    const contactMutation = useEditContactBusinessCardField();

    const editBusinessCardField =
        peepType === 'account'
            ? accountMutation.editAccountBusinessCardField
            : contactMutation.editContactBusinessCardField;

    const patchBusinessCardField = useCallback(
        (input: BusinessCardPatchInput) => {
            setRowErrorMessage(null);
            return editBusinessCardField({
                entityId,
                ...input,
            }).catch((error) => {
                setRowErrorMessage({
                    id: input.attributeValueId,
                    errorMessage: getFieldErrorMessage(input),
                });
                throw error;
            });
        },
        [editBusinessCardField, entityId, setRowErrorMessage]
    );

    return useMemo(
        () => ({
            patchUrl: (input: PatchUrlInput) => {
                return patchBusinessCardField(input);
            },
            patchEmail: (input: PatchEmailInput) => {
                return patchBusinessCardField(input);
            },
            patchPhone: (input: PatchPhoneInput) => {
                const countryCodeAndNumber = input.phoneValue?.countryCodeAndNumber;

                // Strip "+" from input, EAV patch rejects otherwise
                return patchBusinessCardField({
                    ...input,
                    phoneValue: input.phoneValue
                        ? {
                              ...input.phoneValue,
                              countryCodeAndNumber: countryCodeAndNumber
                                  ? countryCodeAndNumber.trim().replace(/^\+/, '')
                                  : countryCodeAndNumber,
                          }
                        : input.phoneValue,
                });
            },
            patchAddress: (input: PatchAddressInput) => {
                return patchBusinessCardField(input).then(() => {
                    if (peepType === 'contact') {
                        contactRefetchForEvent('locationUpdated');
                    } else {
                        accountRefetchForEvent('locationUpdated');
                    }
                });
            },
        }),
        [patchBusinessCardField, peepType]
    );
}
